/**
 * ReportFilters Component
 *
 * Compact filter controls for the unified reports page.
 * Contains date picker dropdown and room selection.
 */

import React, { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, CheckCircle, Target } from 'lucide-react'
import { Room, Session } from '@/types'

interface ReportFiltersProps {
  // Date selection
  selectedDate: Date | null
  currentMonth: Date
  onDateClick: (date: Date) => void
  onMonthNavigate: (direction: 'prev' | 'next') => void
  onGoToMostRecent: () => void
  isDateClickable: (date: Date) => boolean
  calendarDays: (Date | null)[]
  monthNames: string[]
  dayNames: string[]

  // Room selection
  availableRooms: Room[]
  selectedRooms: Set<number>
  onRoomToggle: (roomId: number) => void
  onSelectAllRooms: () => void
}

const ReportFilters: React.FC<ReportFiltersProps> = ({
  selectedDate,
  currentMonth,
  onDateClick,
  onMonthNavigate,
  onGoToMostRecent,
  isDateClickable,
  calendarDays,
  monthNames,
  dayNames,
  availableRooms,
  selectedRooms,
  onRoomToggle,
  onSelectAllRooms,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const datePickerRef = useRef<HTMLDivElement>(null)
  const today = new Date()

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setShowDatePicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDateSelect = (date: Date) => {
    onDateClick(date)
    setShowDatePicker(false)
  }

  const formatSelectedDate = (date: Date | null) => {
    if (!date) return 'Select date'
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4 w-64 flex-shrink-0">
      {/* Date Picker */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Date
        </label>
        <div className="relative" ref={datePickerRef}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className={`text-sm ${selectedDate ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                {formatSelectedDate(selectedDate)}
              </span>
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
          </button>

          {/* Date Picker Dropdown */}
          {showDatePicker && (
            <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-3">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => onMonthNavigate('prev')}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-sm font-semibold text-gray-900">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button
                  onClick={() => onMonthNavigate('next')}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 text-center mb-3">
                {dayNames.map(day => (
                  <div key={day} className="p-1 text-[10px] font-medium text-gray-400 uppercase">
                    {day.charAt(0)}
                  </div>
                ))}

                {calendarDays.map((day, index) => {
                  if (!day) {
                    return <div key={index} className="p-1"></div>
                  }

                  const isClickable = isDateClickable(day)
                  const isToday = day.toDateString() === today.toDateString()
                  const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString()

                  return (
                    <button
                      key={index}
                      onClick={() => isClickable && handleDateSelect(day)}
                      disabled={!isClickable}
                      className={`
                        p-1.5 rounded-lg text-sm transition-all
                        ${isClickable
                          ? 'cursor-pointer hover:bg-primary-100 text-gray-700 font-medium'
                          : 'cursor-not-allowed text-gray-300'
                        }
                        ${isToday && !isSelected ? 'ring-1 ring-primary-300' : ''}
                        ${isSelected ? 'bg-primary-600 text-white hover:bg-primary-700' : ''}
                      `}
                    >
                      {day.getDate()}
                    </button>
                  )
                })}
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-2 flex gap-2">
                <button
                  onClick={() => {
                    onGoToMostRecent()
                    setShowDatePicker(false)
                  }}
                  className="flex-1 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-100 transition-colors"
                >
                  Most Recent
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Room Selection */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Rooms
          </label>
          {selectedDate && availableRooms.length > 1 && (
            <button
              onClick={onSelectAllRooms}
              className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors font-medium"
            >
              <CheckCircle className="h-3 w-3" />
              All
            </button>
          )}
        </div>

        {selectedDate && availableRooms.length > 0 ? (
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {availableRooms.map(room => (
              <label
                key={room.id}
                className={`
                  flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm
                  ${selectedRooms.has(room.id)
                    ? 'bg-primary-50 border-primary-200 text-primary-900'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }
                  ${selectedRooms.has(room.id) && selectedRooms.size <= 1 ? 'opacity-75' : ''}
                `}
              >
                <input
                  type="checkbox"
                  checked={selectedRooms.has(room.id)}
                  onChange={() => onRoomToggle(room.id)}
                  disabled={selectedRooms.has(room.id) && selectedRooms.size <= 1}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                />
                <span className="font-medium truncate">{room.name}</span>
              </label>
            ))}
          </div>
        ) : selectedDate ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Target className="h-6 w-6 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No rooms for this date</p>
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Calendar className="h-6 w-6 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Select a date first</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportFilters
